import React, { useMemo } from 'react';
import '../App.css';
import { getEffectiveScale } from '../gameLogic/gameUtils.js';
import { getUILoc, getCardLoc, getCardTypeName } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';
import CardNameWithRuby from './CardNameWithRuby.js';
import audioManager from '../utils/AudioManager.js';

/**
 * カードアクションメニュー (詳細表示 + プレイボタン)
 */
const CardActionMenu = ({ card, player, gameState, cardDefs, onPlay, onClose }) => {
    // 翻訳データの取得
    const cardLoc = getCardLoc(card.id);
    const cardTypeName = getCardTypeName(card.card_type);

    // 関連カード（トークンやトリガー対象）を抽出
    const relatedCards = useMemo(() => {
        if (!card || !cardDefs) return [];

        const baseDef = cardDefs[card.id] || card;
        const ids = new Set();

        if (Array.isArray(baseDef.related_card_templates)) {
            baseDef.related_card_templates.forEach(id => ids.add(id));
        }

        const findCardTemplates = (obj) => {
            if (!obj || typeof obj !== 'object') return;
            if (obj.card_template_id) ids.add(obj.card_template_id);
            Object.values(obj).forEach(value => {
                if (Array.isArray(value)) value.forEach(item => findCardTemplates(item));
                else if (typeof value === 'object') findCardTemplates(value);
            });
        };

        if (baseDef.triggers) findCardTemplates(baseDef.triggers);

        return Array.from(ids).map(id => cardDefs[id]).filter(Boolean);
    }, [card, cardDefs]);

    if (!card) return null;

    const handleClose = () => {
        audioManager.playSE('cancel');
        onClose();
    };

    const handlePlay = () => {
        onPlay();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    // プレイ可能かどうかの判定
    const canPlay = () => {
        if (!player || gameState.awaiting_input) return false;
        const isPlayerTurn = gameState.current_turn === card.owner;
        if (!isPlayerTurn) return false;

        // 「原始共産制」チェック (ID: 22) - イデオロギー枠を確認
        const isPrimitiveCommunismActive = player.ideology && player.ideology.id === 22;
        if (isPrimitiveCommunismActive && card.card_type === 1) {
            return false;
        }

        const effectiveScale = getEffectiveScale(player, gameState);
        const hasEnoughScale = effectiveScale >= card.required_scale;

        if (card.card_type === 1 && !(player.field.filter(c => c.id === 33).length > 0 && card.id === 33)) {
            const fieldCount = player.field.length;
            const hasFieldSpace = fieldCount < player.field_limit;
            return hasEnoughScale && hasFieldSpace;
        }

        return hasEnoughScale;
    };

    const playable = canPlay();
    const durabilityValue = card.current_durability !== undefined ? card.current_durability : card.durability;

    const getPlayRestriction = () => {
        if (gameState.awaiting_input) return getUILoc('STATUS.AWAITING_INPUT');
        
        // 「原始共産制」チェック (ID: 22) - イデオロギー枠を確認
        const isPrimitiveCommunismActive = player.ideology && player.ideology.id === 22;
        if (isPrimitiveCommunismActive && card.card_type === 1) {
            return getUILoc('STATUS.PRIMITIVE_COMMUNISM_ACTIVE');
        }

        if (getEffectiveScale(player, gameState) < card.required_scale) return getUILoc('STATUS.INSUFFICIENT_SCALE');
        if (card.card_type === 1 && card.id !== 33 && player.field.length >= player.field_limit) return getUILoc('STATUS.FIELD_FULL');
        if (gameState.current_turn !== card.owner) return getUILoc('STATUS.OPPONENT_TURN');
        return getUILoc('STATUS.UNPLAYABLE');
    };

    return (
        <div className="card-detail-overlay" onClick={handleOverlayClick}>
            <div className="card-detail-modal action-menu-modal fixed-footer">
                {/* 背景イラスト演出 (ライブラリと同様) */}
                <CardIllustration cardId={card.id} isBackground={true} />

                <div className="card-detail-header">
                    <div className="card-header-main">
                        <h2>
                            <CardNameWithRuby cardId={card.id} fallbackName={cardLoc.name} />
                        </h2>
                        {card.is_token && <span className="token-badge">TOKEN</span>}
                    </div>
                    <button className="close-button" onClick={handleClose}>×</button>
                </div>

                <div className="card-detail-scroll-area">
                    <div className="card-detail-content">
                        <div className="card-detail-info">
                            <div className="info-row">
                                <span className="info-label">{getUILoc('LABELS.CARD_TYPE')}:</span>
                                <span className="info-value">{cardTypeName}</span>
                            </div>

                            <div className="info-row">
                                <span className="info-label">{getUILoc('LABELS.REQUIRED_SCALE')}:</span>
                                <span className="info-value">{card.required_scale}</span>
                            </div>

                            {card.card_type === 1 && (
                                <div className="info-row">
                                    <span className="info-label">{getUILoc('LABELS.DURABILITY')}:</span>
                                    <span className="info-value">{durabilityValue}/{card.durability}</span>
                                </div>
                            )}
                        </div>

                        <div className="card-detail-description">
                            <h4>{getUILoc('LABELS.EFFECT')}:</h4>
                            <p>{cardLoc.description}</p>
                        </div>

                        {relatedCards.length > 0 && (
                            <div className="related-cards-section">
                                <h4>{getUILoc('LABELS.RELATED_CARDS')}:</h4>
                                <div className="related-cards-list">
                                    {relatedCards.map((rc, idx) => {
                                        const rcLoc = getCardLoc(rc.id);
                                        return (
                                            <div key={idx} className="related-card-item">
                                                <div className="related-card-header">
                                                    <CardIllustration cardId={rc.id} isMini={true} />
                                                    <span className="related-card-name">{rcLoc.name}</span>
                                                    <span className="related-card-type">{getCardTypeName(rc.card_type)}</span>
                                                    {rc.is_token && <span className="token-badge">TOKEN</span>}
                                                </div>
                                                <p className="related-card-description">{rcLoc.description}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="card-action-section">
                    <button 
                        id="play_button"
                        className={`play-action-button ${!playable ? 'disabled' : ''}`}
                        onClick={playable ? handlePlay : undefined}
                        disabled={!playable}
                    >
                        {playable ? getUILoc('BUTTONS.PLAY') : getPlayRestriction()}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CardActionMenu;
